import { useState, useEffect } from 'react';
import type { Book, BookCreate, BulkUpload } from 'types';
import { bookService } from 'services/bookService';
import { compressImage } from 'utils/imageHelper';
import { API_URL } from 'utils/apiUrl';
import './BookFormModal.css';

type UploadMethod = 'automatic-upload' | 'manual-upload' | 'bulk-image';

interface BookFormModalProps {
  book?: Book;
  onClose: () => void;
  onSaved: (book: Book) => void;
  onBulkUpload?: () => void;
  onError?: (error: { type: 'error'; title: string; message: string }) => void;
}

export const BookFormModal = ({ book, onClose, onSaved, onBulkUpload, onError }: BookFormModalProps) => {
  const [uploadMethod, setUploadMethod] = useState<UploadMethod>('automatic-upload');
  const [formData, setFormData] = useState<{
    title: string;
    author_name: string;
    isbn: string;
    publisher: string;
    publication_year: string;
    category: string;
    description: string;
  }>({
    title: '',
    author_name: '',
    isbn: '',
    publisher: '',
    publication_year: '',
    category: '',
    description: '',
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [lookupImagePath, setLookupImagePath] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupMessage, setLookupMessage] = useState('');
  const [lookupSuccess, setLookupSuccess] = useState(false);

  useEffect(() => {
    if (book) {
      setFormData({
        title: book.title,
        author_name: book.author_name,
        isbn: book.isbn || '',
        publisher: book.publisher || '',
        publication_year: book.publication_year?.toString() || '',
        category: book.category || '',
        description: book.description || '',
      });
      const imagePreviewUrl = book.image_path
        ? (book.image_path.startsWith('http') ? book.image_path : `${API_URL}${book.image_path}`)
        : null;
      setImagePreview(imagePreviewUrl);
      setLookupImagePath(null);
    }
  }, [book]);

  const handleIsbnLookup = async (isbn: string) => {
    if (!isbn.trim()) return;

    setIsLookingUp(true);
    setLookupMessage('Looking up book...');
    setLookupSuccess(false);

    try {
      const bookInfo = await bookService.lookupBookByIsbn(isbn);

      const hasData = bookInfo.title || bookInfo.author_name;

      if (hasData) {
        setFormData(prev => ({
          ...prev,
          title: bookInfo.title || prev.title,
          author_name: bookInfo.author_name || prev.author_name,
          category: bookInfo.category || prev.category,
          description: bookInfo.description || prev.description,
          publisher: bookInfo.publisher || prev.publisher,
          publication_year: bookInfo.publication_year?.toString() || prev.publication_year,
        }));

        if (bookInfo.image_path) {
          // New ISBN has image - update it
          const fullImageUrl = bookInfo.image_path.startsWith('http')
            ? bookInfo.image_path
            : `${API_URL}${bookInfo.image_path}`;
          setImagePreview(fullImageUrl);
          setLookupImagePath(bookInfo.image_path);
          setSelectedImage(null);
        } else {
          // New ISBN has no image - clear old image
          if (imagePreview && selectedImage) {
            URL.revokeObjectURL(imagePreview);
          }
          setImagePreview(null);
          setLookupImagePath(null);
          setSelectedImage(null);
        }

        setLookupMessage('Book details loaded successfully!');
        setLookupSuccess(true);
      } else {
        setLookupMessage('No book data found. Please fill manually.');
      }
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setLookupMessage('No book data found. Please fill manually.');
      } else {
        setLookupMessage('Unable to fetch book details. Please fill manually.');
      }
    } finally {
      setIsLookingUp(false);
      setTimeout(() => {
        setLookupMessage('');
      }, 3000);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const bookData: BookCreate & { image?: File | string | null } = {
        title: formData.title,
        author_name: formData.author_name,
        isbn: formData.isbn || undefined,
        publisher: formData.publisher || undefined,
        publication_year: formData.publication_year ? parseInt(formData.publication_year, 10) : undefined,
        category: formData.category || undefined,
        description: formData.description || undefined,
        image: selectedImage || lookupImagePath,
      };

      let savedBook: Book;
      if (book) {
        savedBook = await bookService.updateBook(book.id, bookData);
      } else {
        savedBook = await bookService.createBook(bookData);
      }

      onSaved(savedBook);
    } catch (err: any) {
      console.error('Error saving book:', err);
      const errorDetail = err?.response?.data?.detail || 'Failed to save book';
      
      // Check for duplicate ISBN error and show as popup notification
      const duplicateMatch = errorDetail.match(/Book with ISBN '(.+)' already exists/);
      if (duplicateMatch && onError) {
        const isbn = duplicateMatch[1];
        onError({
          type: 'error',
          title: 'Duplicate ISBN',
          message: `Book with ISBN '${isbn}' already exists in the library`,
        });
        // Clear form and keep modal open for retry
        handleClearErrorAndReset();
      } else {
        setError(errorDetail);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleIsbnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      isbn: value,
    }));
    setLookupMessage('');
    setLookupSuccess(false);
  };

  const handleIsbnKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && formData.isbn && !book && formData.isbn.length >= 10) {
      e.preventDefault();
      await handleIsbnLookup(formData.isbn);
    }
  };

  const handleIsbnBlur = async () => {
    if (formData.isbn && !book && formData.isbn.length >= 10) {
      await handleIsbnLookup(formData.isbn);
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const originalFile = files[0];
      try {
        // Compress image before storing
        const compressedFile = await compressImage(originalFile, 300);
        setSelectedImage(compressedFile);
        setImagePreview(URL.createObjectURL(compressedFile));
      } catch (err) {
        console.error('Error compressing image:', err);
        // Use original file if compression fails
        setSelectedImage(originalFile);
        setImagePreview(URL.createObjectURL(originalFile));
      }
    }
  };

  const handleRemoveMainImage = () => {
    if (imagePreview && selectedImage) {
      URL.revokeObjectURL(imagePreview);
    }
    setSelectedImage(null);
    setImagePreview(null);
    setLookupImagePath(null);
  };

  const handleClose = () => {
    if (imagePreview && selectedImage) {
      URL.revokeObjectURL(imagePreview);
    }
    // Clear all form state when closing
    setError('');
    setFormData({
      title: '',
      author_name: '',
      isbn: '',
      publisher: '',
      publication_year: '',
      category: '',
      description: '',
    });
    setSelectedImage(null);
    setImagePreview(null);
    setLookupImagePath(null);
    setLookupMessage('');
    setLookupSuccess(false);
    setIsSubmitting(false);
    setIsLookingUp(false);
    onClose();
  };

  const handleClearErrorAndReset = () => {
    // Clear error and form data but keep modal open
    setError('');
    setFormData({
      title: '',
      author_name: '',
      isbn: '',
      publisher: '',
      publication_year: '',
      category: '',
      description: '',
    });
    setSelectedImage(null);
    setImagePreview(null);
    setLookupImagePath(null);
    setLookupMessage('');
    setLookupSuccess(false);
    setIsSubmitting(false);
    setIsLookingUp(false);
  };

  const handleMethodChange = (method: UploadMethod) => {
    setUploadMethod(method);
    // Reset form state when switching methods (only in create mode)
    if (!book) {
      // Clean up object URLs
      if (imagePreview && selectedImage) {
        URL.revokeObjectURL(imagePreview);
      }
      setFormData({
        title: '',
        author_name: '',
        isbn: '',
        publisher: '',
        publication_year: '',
        category: '',
        description: '',
      });
      setSelectedImage(null);
      setImagePreview(null);
      setLookupImagePath(null);
      setLookupMessage('');
      setLookupSuccess(false);
      setError('');
    }
  };

  return (
    <div className="modal-overlay" onClick={error ? handleClearErrorAndReset : handleClose}>
      <div className="modal-content book-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {book ? 'Edit Book' : 'Add New Book'}
          </h2>
          <button className="modal-close" onClick={error ? handleClearErrorAndReset : handleClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Upload Method Tabs */}
        {!book && (
          <div className="upload-method-tabs">
            <button
              className={`tab-btn ${uploadMethod === 'automatic-upload' ? 'active' : ''}`}
              onClick={() => handleMethodChange('automatic-upload')}
            >
              <span className="material-symbols-outlined">barcode_reader</span>
              Automatic Upload
            </button>
            <button
              className={`tab-btn ${uploadMethod === 'manual-upload' ? 'active' : ''}`}
              onClick={() => handleMethodChange('manual-upload')}
            >
              <span className="material-symbols-outlined">edit_note</span>
              Manual Upload
            </button>
            <button
              className={`tab-btn ${uploadMethod === 'bulk-image' ? 'active' : ''}`}
              onClick={() => {
                handleClose();
                if (onBulkUpload) onBulkUpload();
              }}
            >
              <span className="material-symbols-outlined">qr_code_scanner</span>
              Bulk Image Upload
            </button>
          </div>
        )}

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="alert alert-error">
                <span className="material-symbols-outlined">error</span>
                <div>
                  <strong>Error:</strong> {error}
                </div>
              </div>
            )}

            {/* Method-specific info banners (Create mode only) */}
            {!book && uploadMethod === 'automatic-upload' && (
              <div className="alert alert-info">
                <span className="material-symbols-outlined">info</span>
                <div>
                  <strong>Automatic Upload:</strong> Enter the ISBN number or upload barcode image to automatically fetch book details.
                </div>
              </div>
            )}
            {!book && uploadMethod === 'manual-upload' && (
              <div className="alert alert-info">
                <span className="material-symbols-outlined">info</span>
                <div>
                  <strong>Manual Upload:</strong> Manually enter all book information and upload cover image.
                </div>
              </div>
            )}

            <div className="form-content-wrapper">
              {/* Left - Image Section */}
              <div className="cover-image-section">
                <div className="form-group">
                  <label className="form-label">Book Cover Image</label>
                  {imagePreview ? (
                    <div className="cover-preview">
                      <img src={imagePreview} alt="Cover preview" />
                      <button type="button" className="btn-remove-image" onClick={handleRemoveMainImage}>
                        <span className="material-symbols-outlined">close</span>
                      </button>
                    </div>
                  ) : (
                    <div className="cover-preview-empty">
                      {formData.title && formData.author_name ? (
                        <div className="placeholder-preview-content">
                          <span className="placeholder-preview-title">{formData.title}</span>
                          <span className="placeholder-preview-author">{formData.author_name}</span>
                        </div>
                      ) : (
                        <span className="material-symbols-outlined">menu_book</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Edit Mode - Cover Image Upload Only */}
                {book && (
                  <div className="form-group">
                    <label className="form-label">
                      Upload New Cover Image
                      <span className="hint"> (Optional - replaces current cover)</span>
                    </label>
                    <div className="image-upload-wrapper">
                      <input
                        type="file"
                        id="cover_image"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="form-file-input"
                      />
                      <label htmlFor="cover_image" className="btn btn-primary btn-sm">
                        <span className="material-symbols-outlined">upload</span>
                        Upload Cover
                      </label>
                    </div>
                  </div>
                )}

                {/* Automatic Upload Method - Barcode Upload (Create mode only) */}
                {!book && uploadMethod === 'automatic-upload' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">
                        Upload Barcode Image
                        <span className="hint"> (Optional - to extract ISBN)</span>
                      </label>
                      <div className="image-upload-wrapper">
                        <input
                          type="file"
                          id="barcode_isbn"
                          accept="image/*"
                          onChange={async (e) => {
                            const files = e.target.files;
                            if (files && files.length > 0) {
                              const originalFile = files[0];
                              let fileToUse = originalFile;
                              
                              // Compress image before processing
                              try {
                                fileToUse = await compressImage(originalFile, 300);
                              } catch (err) {
                                console.error('Error compressing image:', err);
                                // Use original file if compression fails
                              }
                              
                              setIsLookingUp(true);
                              try {
                                const result = await bookService.extractISBNFromImage(fileToUse);
                                if (result.isbns && result.isbns.length > 0) {
                                  setFormData(prev => ({ ...prev, isbn: result.isbns[0] }));
                                  setLookupMessage(`ISBN extracted: ${result.isbns[0]}`);
                                  setLookupSuccess(true);
                                  // Auto lookup
                                  await handleIsbnLookup(result.isbns[0]);
                                } else {
                                  setLookupMessage('No ISBN detected. Please enter manually.');
                                }
                              } catch (err) {
                                setLookupMessage('Error reading image. Please enter ISBN manually.');
                              } finally {
                                setIsLookingUp(false);
                              }
                            }
                          }}
                          className="form-file-input"
                        />
                        <label htmlFor="barcode_isbn" className="btn btn-secondary btn-sm">
                          <span className="material-symbols-outlined">upload</span>
                          Upload Barcode
                        </label>
                      </div>
                      {isLookingUp && (
                        <div className="lookup-message">
                          <span className="material-symbols-outlined loading-icon">progress_activity</span>
                          Processing image...
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Manual Upload Method - Cover Image Upload (Create mode only) */}
                {!book && uploadMethod === 'manual-upload' && (
                  <div className="form-group">
                    <label className="form-label">
                      Upload Cover Image
                      <span className="hint"> (Optional)</span>
                    </label>
                    <div className="image-upload-wrapper">
                      <input
                        type="file"
                        id="cover_image_manual"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="form-file-input"
                      />
                      <label htmlFor="cover_image_manual" className="btn btn-primary btn-sm">
                        <span className="material-symbols-outlined">upload</span>
                        Upload Cover
                      </label>
                    </div>
                  </div>
                )}

                {/* Bulk Image Upload Info (Create mode only) */}
                {!book && uploadMethod === 'bulk-image' && (
                  <div className="bulk-upload-info">
                    <span className="material-symbols-outlined">qr_code_scanner</span>
                    <p>Opening Bulk Image Upload...</p>
                  </div>
                )}
              </div>

              {/* Right - Form Fields */}
              <div className="form-fields-section">
                <div className="form-grid-compact">
                  {/* ISBN Field - Show for automatic-upload, manual-upload, and edit mode */}
                  {(uploadMethod === 'automatic-upload' || uploadMethod === 'manual-upload' || book) && (
                    <div className="form-group isbn-lookup-group">
                      <label htmlFor="isbn" className="form-label">
                        ISBN
                      </label>
                      <div className="isbn-input-wrapper">
                        <input
                          type="text"
                          id="isbn"
                          name="isbn"
                          className="form-input"
                          value={formData.isbn}
                          onChange={handleIsbnChange}
                          onKeyDown={handleIsbnKeyDown}
                          onBlur={handleIsbnBlur}
                          placeholder="e.g., 978-0743273565"
                          disabled={isLookingUp}
                        />
                        {isLookingUp && (
                          <span className="material-symbols-outlined loading-icon">progress_activity</span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="title" className="form-label">
                      Title <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      className="form-input"
                      value={formData.title}
                      onChange={handleChange}
                      required
                      placeholder="Enter book title"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="author_name" className="form-label">
                      Author Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="author_name"
                      name="author_name"
                      className="form-input"
                      value={formData.author_name}
                      onChange={handleChange}
                      required
                      placeholder="Enter author name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="category" className="form-label">
                      Category
                    </label>
                    <input
                      type="text"
                      id="category"
                      name="category"
                      className="form-input"
                      value={formData.category}
                      onChange={handleChange}
                      placeholder="e.g., Fiction, Science"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="publisher" className="form-label">
                      Publisher
                    </label>
                    <input
                      type="text"
                      id="publisher"
                      name="publisher"
                      className="form-input"
                      value={formData.publisher}
                      onChange={handleChange}
                      placeholder="Enter publisher name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="publication_year" className="form-label">
                      Publication Year
                    </label>
                    <input
                      type="number"
                      id="publication_year"
                      name="publication_year"
                      className="form-input"
                      value={formData.publication_year || ''}
                      onChange={handleChange}
                      placeholder="e.g., 2020"
                      min="1000"
                      max="2100"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description" className="form-label">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                className="form-textarea"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter book description..."
                rows={2}
              />
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={error ? handleClearErrorAndReset : handleClose}>
                {error ? 'Clear & Retry' : 'Cancel'}
              </button>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : (book ? 'Update Book' : 'Add Book')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
